import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect } from 'react';
import { Formik } from 'formik';
import { AddSignerHandler } from './types';
import { usePendingData } from '@/providers/PendingData.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useStorage from '@/hooks/useStorage';
import useFetchSigningAuthorityInfo from '@/hooks/useApi/useFetchSigningAuthorityInfo';
import buildAddSignerRows from './buildAddSignerRows.helper';

const { Box, Button, Table, TextField } = vp;

type AddSignerProps = {
  handlers: AddSignerHandler;
  defaultValues: vp.AddSignerRequest;
};

type FC = FunctionComponent<AddSignerProps>;
const AddSignerForm: FC = (props: AddSignerProps) => {
  const { handlers, defaultValues } = props;
  const { updatePendingData } = usePendingData();
  const addSignerRows = buildAddSignerRows();
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const {
    accountToken: { getAccountToken },
  } = useStorage();
  const accountToken = getAccountToken();

  const [signingAuthorityInfo, fetchSigningAuthorityInfo] = useFetchSigningAuthorityInfo({
    bearer: accountToken || '',
  });

  useEffect(() => {
    fetchSigningAuthorityInfo();
  }, [accountToken]);

  return (
    <Formik initialValues={defaultValues} onSubmit={handlers.onAddSigner} validationSchema={createdValidationSchema()}>
      {({ touched, errors, handleChange, handleBlur, handleSubmit, values }) => {
        useEffect(() => {
          updatePendingData(values);
        }, [values]);

        const addSigner: vp.TableProps = {
          name: 'add-signer-fields',
          variant: 'pair',
          columns: {
            Field: { header: '' },
            Input: { header: '' },
          },
          rows: addSignerRows.map(({ label, id, preset }) => ({
            Field: label,
            Input: (
              <TextField
                key={id}
                name={id}
                preset={preset}
                variant="standard"
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] && touched[id] ? errors[id] : null}
              />
            ),
          })),
        };

        return (
          <form onSubmit={handleSubmit}>
            <Table {...addSigner} />
            <Box mt="lg" jc="fs">
              <Button
                id="back-to-signers-btn"
                label="Submit"
                color="primary"
                width="223px"
                type="submit"
                isDisabled={isRoleViewOnly}
                noShadow
                round
              />
            </Box>
          </form>
        );
      }}
    </Formik>
  );
  function createdValidationSchema() {
    return Yup.object().shape({
      AuthorizedFullLegalName: Yup.string()
        .required('Please fill out all mandatory fields')
        .matches(/^[A-Za-z0-9 ]+$/, "This field cannot contain any of special characters. eg) ? ! / < ~ ` ' > <")
        .matches(/^\S+(?:\s+\S+)+$/, "Please enter the signer's full legal name (first and last name)."),
      AuthorizedOccupation: Yup.string()
        .required('Please fill out all mandatory fields')
        .matches(/^[A-Za-z0-9 ]+$/, "This field cannot contain any of special characters. eg) ? ! / < ~ ` ' > <"),
      AuthorizedEmailAddress: Yup.string()
        .matches(
          /^(.*)@(?!(gmail|mail|email|usa|myself|consultant|europe|post|asia|iname|writeme|dr|cheerful|accountant|techie|engineer|linuxmail|musician|workmail|financier|fastservice|contractor|planetmail|homemail|hackermail|dbzmail|coolsite|australiamail|pacificwest|nycmail|kissfans|technologist|webname|null|greenmail|instructor|secratery|salesperson|repairman|deliveryman|allergist|execs|auctioneer|bartender|comic|yahoo|hotmail|msn|protonmail|zohomail|outlook|icloud|aol|tutanota|startmail|hushmail|mailfence|aweber|countermail|posteo|mail2one|mail2world|mail2usa|mail2seattle|mail2washington|mail2cool|mail2art|mail2fun|mail2dude|mail2expert|mail2consultant|thunderbird|yandex|ranren|mailasia|gmx|xwebtools|netscape|fexpost)).(.*)/,
          'Please provide a valid Email Address',
        )
        .test('email-validation', 'The email address already exists.', (value) => {
          const newSignerValidation = Object.values(signingAuthorityInfo.SigningAuthority || {})
            .map((signer) => signer.EmailAddress)
            .includes(value || '');

          return !newSignerValidation;
        })
        .required('Please fill out all mandatory fields')
        .email('Please provide a valid Email Address'),
    });
  }
};

export default AddSignerForm;
